import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Alert, Tabs, Tab, Box, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Backdrop, CircularProgress, Chip,
  useTheme, useMediaQuery,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import axios from 'axios';

/**
 * Parse a measurements string like "9.89 - 6.98 * 4.60" or "6.5×6.5×4.0"
 * into { length, width, height } for CSV preview display.
 */
function parseMeasurementsPreview(str) {
  if (!str || typeof str !== 'string') return null;
  const s = String(str).trim().replace(/×/g, ' ').replace(/\*/g, ' ').replace(/,/g, ' ');
  const parts = s.split(/\s+/).map(p => parseFloat(p)).filter(n => !Number.isNaN(n) && n >= 0);
  if (parts.length >= 3) return { length: parts[0], width: parts[1], height: parts[2] };
  if (parts.length === 2) return { length: parts[0], width: parts[1], height: null };
  if (parts.length === 1) return { length: parts[0], width: null, height: null };
  return null;
}

/**
 * Standalone upload CSV dialog, fully self-contained with its own state.
 *
 * Props:
 *   open            – boolean
 *   onClose         – () => void  (called after cancel or after results are dismissed)
 *   defaultUploadType – 'certified' | 'nonCertified'
 *   onImportSuccess – () => void  (called after a successful import so the parent can refresh)
 */
const UploadCsvDialog = ({ open, onClose, defaultUploadType = 'certified', onImportSuccess }) => {
  const lockUploadType = defaultUploadType === 'certifiedFancy';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [uploadType, setUploadType] = useState(defaultUploadType);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [uploadPreviewRows, setUploadPreviewRows] = useState(null);
  const [uploadPreviewValidations, setUploadPreviewValidations] = useState([]);
  const [uploadPreviewLoading, setUploadPreviewLoading] = useState(false);
  const [uploadValidating, setUploadValidating] = useState(false);
  const [uploadImporting, setUploadImporting] = useState(false);
  const [uploadImportResult, setUploadImportResult] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  // Reset internal state when dialog opens / defaultUploadType changes
  useEffect(() => {
    if (open) {
      setUploadType(defaultUploadType);
      setUploadFile(null);
      setUploadPreviewRows(null);
      setUploadPreviewValidations([]);
      setUploadImportResult(null);
      setUploadError('');
      setUploadSuccess('');
    }
  }, [open, defaultUploadType]);

  // Validate preview rows whenever they change
  useEffect(() => {
    if (!uploadPreviewRows || uploadPreviewRows.length === 0) {
      setUploadPreviewValidations([]);
      return;
    }
    let cancelled = false;
    setUploadValidating(true);
    const endpoint = uploadType === 'nonCertified' ? '/api/upload/csv/non-certified/validate' : '/api/upload/csv/validate';
    const payload = { rows: uploadPreviewRows };
    if (uploadType === 'certifiedFancy') payload.defaultColorCategory = 'fancy';
    axios.post(endpoint, payload)
      .then((res) => { if (!cancelled) setUploadPreviewValidations(res.data.validations || []); })
      .catch(() => { if (!cancelled) setUploadPreviewValidations([]); })
      .finally(() => { if (!cancelled) setUploadValidating(false); });
    return () => { cancelled = true; };
  }, [uploadPreviewRows, uploadType]);

  const handleClose = () => {
    if (uploadImporting) return;
    setUploadFile(null);
    setUploadPreviewRows(null);
    setUploadPreviewValidations([]);
    setUploadImportResult(null);
    setUploadError('');
    onClose();
  };

  const switchUploadType = (type) => {
    setUploadType(type);
    setUploadFile(null);
    setUploadPreviewRows(null);
    setUploadPreviewValidations([]);
    setUploadImportResult(null);
    setUploadError('');
  };

  const downloadCsvTemplate = async () => {
    if (uploadType === 'nonCertified') {
      try {
        const res = await axios.get('/api/upload/csv/template/non-certified', { responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'non_certified_stones_template.csv';
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        setUploadError(err.response?.data?.error || 'Failed to download template');
      }
      return;
    }
    if (uploadType === 'certifiedFancy') {
      const headers = [
        'certificateNumber', 'sku', 'shipmentId', 'shipmentItemID', 'shape', 'carat', 'color',
        'fancyColorIntensity', 'fancyColorHue', 'fancyColorOvertone', 'clarity', 'cut', 'lab', 'basePrice', 'buyPricePerCarat',
        'measurements', 'length', 'width', 'height', 'polish', 'symmetry', 'diamondType', 'status',
      ];
      const exampleRowOneLine = [
        'GIA-FANCY-001', 'INV-F-001', 'SHIP-42', 'SHIP-42-002', 'CUSHION', '1.20',
        'FVP', '', '', '', 'VS2', 'EX', 'GIA', '12000', '10000',
        '6.5×6.5×4.0', '6.5', '6.5', '4.0', 'EX', 'EX', 'Natural', 'available',
      ];
      const exampleRowProse = [
        'GIA-FANCY-002', 'INV-F-002', 'SHIP-42', 'SHIP-42-003', 'PEAR', '0.90',
        'Fancy vivid Pink', '', '', '', 'VS1', 'EX', 'GIA', '15000', '12000',
        '6.0×4.0×2.5', '6.0', '4.0', '2.5', 'EX', 'VG', 'Natural', 'available',
      ];
      const csv = [headers.join(','), exampleRowOneLine.join(','), exampleRowProse.join(',')].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'fancy_diamonds_template.csv';
      link.click();
      URL.revokeObjectURL(url);
      return;
    }
    const headers = [
      'certificateNumber', 'sku', 'shipmentId', 'shipmentItemID', 'shape', 'carat', 'color', 'clarity', 'cut', 'lab', 'basePrice', 'buyPricePerCarat',
      'measurements', 'length', 'width', 'height', 'polish', 'symmetry', 'diamondType', 'status',
    ];
    const exampleRow = [
      'GIA-12345678', 'INV-001', 'SHIP-42', 'SHIP-42-001', 'ROUND', '1.00', 'E', 'VS1', 'EX', 'GIA', '5000', '4500',
      '6.5×6.5×4.0', '6.5', '6.5', '4.0', 'EX', 'EX', 'Natural', 'available',
    ];
    const csv = [headers.join(','), exampleRow.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'diamonds_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleFileSelected = async (file) => {
    if (!file) { setUploadFile(null); setUploadPreviewRows(null); return; }
    setUploadFile(file);
    setUploadPreviewRows(null);
    setUploadError('');
    setUploadPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const endpoint = uploadType === 'nonCertified' ? '/api/upload/csv/non-certified/preview' : '/api/upload/csv/preview';
      const res = await axios.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadPreviewRows(res.data.rows || []);
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Failed to parse file');
      setUploadPreviewRows([]);
    } finally {
      setUploadPreviewLoading(false);
    }
  };

  const handleImportApproved = async () => {
    if (!uploadPreviewRows || uploadPreviewRows.length === 0) return;
    setUploadError('');
    setUploadImporting(true);
    setUploadImportResult(null);
    try {
      const endpoint = uploadType === 'nonCertified' ? '/api/upload/csv/non-certified/import' : '/api/upload/csv/import';
      const importPayload = { rows: uploadPreviewRows };
      if (uploadType === 'certifiedFancy') importPayload.defaultColorCategory = 'fancy';
      const res = await axios.post(endpoint, importPayload);
      setUploadImportResult(res.data);
      setUploadSuccess(`Import completed: ${res.data.successful} succeeded, ${res.data.failed} failed.`);
      if (onImportSuccess) onImportSuccess(uploadType);
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Import failed');
    } finally {
      setUploadImporting(false);
    }
  };

  const downloadImportResults = () => {
    if (!uploadImportResult) return;
    const { results = [], errors = [], totalRows } = uploadImportResult;
    const isNonCertified = results.some((r) => r.action != null);
    const byRow = {};
    let lines;
    if (isNonCertified) {
      results.forEach((r) => {
        byRow[r.row] = {
          status: 'Success', action: r.action || '', shape: r.shape || '',
          measurement: r.measurement || '',
          detail: r.addedCarat != null ? `+${r.addedCarat} ct` : (r.totalCarat != null ? `${r.totalCarat} ct` : ''),
          message: '',
        };
      });
      errors.forEach((e) => { byRow[e.row] = { status: 'Failed', action: '', shape: '', measurement: '', detail: '', message: e.message || '' }; });
      lines = ['Row #,Status,Action,Shape,Measurement,Detail,Message'];
      for (let r = 1; r <= totalRows; r++) {
        const x = byRow[r] || { status: 'Skipped', action: '', shape: '', measurement: '', detail: '', message: '' };
        lines.push(`${r},${x.status},${x.action},"${(x.shape || '').replace(/"/g, '""')}","${(x.measurement || '').replace(/"/g, '""')}",${(x.detail || '').replace(/"/g, '""')},"${(x.message || '').replace(/"/g, '""')}"`);
      }
    } else {
      results.forEach((r) => { byRow[r.row] = { status: 'Success', certificateNumber: r.certificateNumber, message: '' }; });
      errors.forEach((e) => { byRow[e.row] = { status: 'Failed', certificateNumber: '', message: e.message || '' }; });
      lines = ['Row #,Certificate #,Status,Message'];
      for (let r = 1; r <= totalRows; r++) {
        const x = byRow[r] || { status: 'Skipped', certificateNumber: '', message: '' };
        lines.push(`${r},"${(x.certificateNumber || '').replace(/"/g, '""')}",${x.status},"${(x.message || '').replace(/"/g, '""')}"`);
      }
    }
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `import_results_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <Backdrop open={uploadImporting} sx={{ zIndex: (t) => t.zIndex.drawer + 1, color: '#fff', flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={48} />
        <Typography variant="h6">Importing diamonds…</Typography>
      </Backdrop>
      <DialogTitle>Upload CSV</DialogTitle>
      <DialogContent>
        {uploadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>{uploadError}</Alert>}
        {uploadSuccess && <Alert severity="success" sx={{ mb: 2 }}>{uploadSuccess}</Alert>}

        {!lockUploadType && (
          <Tabs
            value={uploadType === 'nonCertified' ? 1 : 0}
            onChange={(_, v) => switchUploadType(v === 0 ? 'certified' : 'nonCertified')}
            sx={{ mb: 2 }}
          >
            <Tab label="Certified diamonds" />
            <Tab label="Non-certified stones" />
          </Tabs>
        )}

        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadCsvTemplate} fullWidth sx={{ mb: 2 }}>
          {uploadType === 'nonCertified' ? 'Download non-certified template' : 'Download CSV template'}
        </Button>

        {uploadPreviewLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CircularProgress />
            <Typography color="text.secondary">Parsing file…</Typography>
          </Box>
        )}

        {!uploadPreviewLoading && uploadPreviewRows && uploadPreviewRows.length > 0 && !uploadImportResult && (
          <Box sx={{ mt: 2 }}>
            {uploadValidating && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Validating rows…</Typography>
            )}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Preview: {uploadPreviewRows.length} row(s).{' '}
              {uploadPreviewValidations.filter(v => !v.ok).length > 0
                ? `${uploadPreviewValidations.filter(v => !v.ok).length} row(s) have errors.`
                : uploadType === 'nonCertified'
                  ? 'Matching shape+measurement will add weight to existing lots.'
                  : 'Review and approve to add to inventory.'}
            </Typography>
            <TableContainer sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {uploadType === 'nonCertified' ? (
                      <>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>#</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>SKU</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Shape</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Measurement</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Total carat</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Price/ct</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Buy price/ct</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Carat/stone</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 220 }}>Error</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>#</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Certificate #</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>SKU</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Shipment ID</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Shipment Item ID</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Shape</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Carat</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Color</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Clarity</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Cut</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Lab</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Price/ct</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Buy price/ct</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Length</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Width</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Height</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Measurements</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Polish</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Symmetry</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Type</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 220 }}>Error</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploadType === 'nonCertified'
                    ? uploadPreviewRows.map((row, idx) => {
                        const validation = uploadPreviewValidations[idx];
                        const hasError = validation && !validation.ok;
                        const isValidating = uploadValidating || !validation;
                        return (
                          <TableRow key={idx} sx={{ backgroundColor: hasError ? 'rgba(211,47,47,0.08)' : undefined }}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>
                              {isValidating
                                ? <Chip label="…" size="small" variant="outlined" />
                                : hasError
                                  ? <Chip label="Error" color="error" size="small" />
                                  : <Chip label="Valid" color="success" size="small" />}
                            </TableCell>
                            <TableCell>{row.sku || '—'}</TableCell>
                            <TableCell>{row.shape}</TableCell>
                            <TableCell>{row.length != null && row.width != null ? `${row.length}×${row.width}` : (row.measurement || '—')}</TableCell>
                            <TableCell>{row.totalCarat != null && row.totalCarat !== '' ? row.totalCarat : '—'}</TableCell>
                            <TableCell>{row.pricePerCarat != null && row.pricePerCarat !== '' ? `$${Number(row.pricePerCarat).toLocaleString()}` : '—'}</TableCell>
                            <TableCell>{row.buyPricePerCarat != null && row.buyPricePerCarat !== '' ? `$${Number(row.buyPricePerCarat).toLocaleString()}` : '—'}</TableCell>
                            <TableCell>{row.caratPerStone != null && row.caratPerStone !== '' ? row.caratPerStone : '—'}</TableCell>
                            <TableCell sx={{ maxWidth: 220, color: hasError ? 'error.main' : 'text.secondary', fontSize: '0.75rem' }} title={validation?.message || ''}>
                              {validation?.message || (validation?.ok ? 'OK' : '—')}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : uploadPreviewRows.map((row, idx) => {
                        const validation = uploadPreviewValidations[idx];
                        const hasError = validation && !validation.ok;
                        const hasLWH = row.length != null && row.length !== '' && row.width != null && row.width !== '' && row.height != null && row.height !== '';
                        const parsed = hasLWH ? null : parseMeasurementsPreview(row.measurements);
                        const len = hasLWH ? row.length : (parsed?.length ?? '—');
                        const wid = hasLWH ? row.width : (parsed?.width ?? '—');
                        const hgt = hasLWH ? row.height : (parsed?.height ?? '—');
                        return (
                          <TableRow key={idx} sx={{ backgroundColor: hasError ? 'rgba(211,47,47,0.08)' : undefined }}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{row.certificateNumber}</TableCell>
                            <TableCell>{row.sku || '—'}</TableCell>
                            <TableCell>{row.shipmentId || '—'}</TableCell>
                            <TableCell>{row.shipmentItemID || '—'}</TableCell>
                            <TableCell>{row.shape}</TableCell>
                            <TableCell>{row.carat}</TableCell>
                            <TableCell>{row.color}</TableCell>
                            <TableCell>{row.clarity}</TableCell>
                            <TableCell>{row.cut}</TableCell>
                            <TableCell>{(row.lab || '').toUpperCase()}</TableCell>
                            <TableCell>{row.basePrice != null && row.basePrice !== '' ? `$${Number(row.basePrice).toLocaleString()}` : '—'}</TableCell>
                            <TableCell>{row.buyPricePerCarat != null && row.buyPricePerCarat !== '' ? `$${Number(row.buyPricePerCarat).toLocaleString()}` : '—'}</TableCell>
                            <TableCell>{len !== '—' ? len : '—'}</TableCell>
                            <TableCell>{wid !== '—' ? wid : '—'}</TableCell>
                            <TableCell>{hgt !== '—' ? hgt : '—'}</TableCell>
                            <TableCell>{row.measurements || '—'}</TableCell>
                            <TableCell>{row.polish || '—'}</TableCell>
                            <TableCell>{row.symmetry || '—'}</TableCell>
                            <TableCell>{row.diamondType || '—'}</TableCell>
                            <TableCell>{row.status || 'available'}</TableCell>
                            <TableCell sx={{ maxWidth: 220, color: hasError ? 'error.main' : 'text.secondary', fontSize: '0.75rem' }} title={validation?.message || ''}>
                              {validation?.message || (validation?.ok ? 'OK' : '—')}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {uploadImportResult && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={uploadImportResult.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {uploadImportResult.message}. {uploadImportResult.successful} row(s) added, {uploadImportResult.failed} failed.
            </Alert>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadImportResults}>
                Download results file
              </Button>
              <Button variant="contained" onClick={handleClose}>Close</Button>
            </Box>
          </Box>
        )}

        {!uploadPreviewLoading && (!uploadPreviewRows || uploadPreviewRows.length === 0) && !uploadImportResult && (
          <Box
            component="label"
            onDragOver={(e) => { e.preventDefault(); setUploadDragOver(true); }}
            onDragLeave={() => setUploadDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setUploadDragOver(false);
              const file = e.dataTransfer?.files?.[0];
              if (file && (file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv')) {
                handleFileSelected(file);
              }
            }}
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              minHeight: 120, mt: 2, px: 2, py: 3, border: '2px dashed',
              borderColor: uploadDragOver ? 'primary.main' : 'divider',
              borderRadius: 2,
              bgcolor: uploadDragOver ? 'action.hover' : 'action.selected',
              cursor: 'pointer', transition: 'border-color 0.2s, background-color 0.2s',
            }}
          >
            <input
              type="file"
              accept=".csv"
              data-testid="upload-csv-file"
              onChange={(e) => handleFileSelected(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {uploadFile ? uploadFile.name : 'Drag a CSV file here or click to browse'}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!uploadImportResult && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            {uploadPreviewRows && uploadPreviewRows.length > 0 && !uploadImporting && (
              <Button onClick={handleImportApproved} variant="contained" color="primary" disabled={uploadValidating} data-testid="upload-csv-import">
                Import to inventory
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadCsvDialog;
